import {
  WSElement,
  WSFlexBox,
  WSGrid,
  WSPill,
  WSText
} from "@wingspanhq/fe-component-library";
import { InsuranceMonitorConfiguration } from "@wingspanhq/wingspan-api-sdk";
import { useMemo } from "react";

import { useQueryFeatureFlags } from "@hooks/useQueryFeatureFlags";
import { ExtendedRequirementDefinition } from "../../../../../query/requirementDefinitions/query/useQueryExtendedRequirementDefinition";
import { selectorIsContextTypesMatched } from "../../../../Requirements/modules/InsuranceCoverage/selectors/selectorIsContextTypesMatched";
import { selectorIsPolicyTypesMatched } from "../../../../Requirements/modules/InsuranceCoverage/selectors/selectorIsPolicyTypesMatched";
import { mapRequirementDefinitionTemplateTierConfig } from "../../../config/maps";
import { getRequirementDefinitionTemplates } from "../../../utils/getRequirementDefinitionTemplates";
import {
  RequirementDefinitionTemplate,
  RequirementDefinitionTemplateTier
} from "../types";

import { Item } from "./Item";

type Props = {
  tier: RequirementDefinitionTemplateTier;
  onSelect: (item: RequirementDefinitionTemplate) => void;
  selected: RequirementDefinitionTemplate | null;
  requirements: ExtendedRequirementDefinition[];
};

export const Section: React.FC<Props> = props => {
  const queryFeatureFlags = useQueryFeatureFlags();
  const existingRequirements = props.requirements;

  const { isWayfinder } =
    mapRequirementDefinitionTemplateTierConfig[props.tier];

  const isSmartAdvancedEnabled = queryFeatureFlags.data?.wayfinderSmartAdvanced;

  const filteredItems = useMemo(
    () =>
      getRequirementDefinitionTemplates().filter(
        item =>
          item.tier === props.tier &&
          (item.isSmartAdvanced ? isSmartAdvancedEnabled : true)
      ),
    [props.tier, isSmartAdvancedEnabled]
  );

  return (
    <WSElement>
      <WSFlexBox alignItems="center" gap="M" mb="L">
        <WSText.Caption weight="medium">
          {mapRequirementDefinitionTemplateTierConfig[props.tier].name}
        </WSText.Caption>

        {isWayfinder ? (
          <WSPill icon="compass" outline text="Wayfinder" theme="violet" />
        ) : null}
      </WSFlexBox>

      <WSGrid>
        {filteredItems.map(item => {
          const existingRequirement = existingRequirements.find(r => {
            const isRequirementTypeMatched = item.requirementType === r.type;

            const isContextTypeMatched = item.contextType
              ? selectorIsContextTypesMatched(
                  r.dataSourceSettings,
                  item.contextType
                )
              : true;

            const isPolicyTypeMatched = item.policyType
              ? selectorIsPolicyTypesMatched({
                  insuranceMonitorConfiguration:
                    r.dataSourceSettings as InsuranceMonitorConfiguration,
                  policyTypes: item.policyType
                })
              : true;

            return (
              isRequirementTypeMatched &&
              isContextTypeMatched &&
              isPolicyTypeMatched
            );
          });

          return (
            <WSGrid.Item key={item.key} span={{ s: "6", m: "4" }}>
              <Item
                existingRequirementId={
                  item.singleInstance ? existingRequirement?.id : undefined
                }
                itemConfig={item}
                selected={props.selected}
                tierConfig={
                  mapRequirementDefinitionTemplateTierConfig[props.tier]
                }
                onSelect={props.onSelect}
              />
            </WSGrid.Item>
          );
        })}
      </WSGrid>
    </WSElement>
  );
};
